import React from 'react';

import { Button } from '@/design-system/button';
import { FormModal, type FormModalTone } from '@/design-system/form-modal';

export interface BulkConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void> | void;
  isLoading?: boolean;
  count: number;
  /** "Allocate", "Mark as Returned", etc. */
  actionLabel: string;
  /** "Proses paket Allocating?" */
  title: string;
  /** Plain-text description shown in the body. */
  description: string;
  /** Affirmative button copy, e.g. "Ya, Proses". */
  confirmLabel: string;
  icon?: string;
  iconTone?: FormModalTone;
  /** Optional danger styling for destructive confirmations. */
  variant?: 'primary' | 'danger';
}

/**
 * Simple Yes/No confirm for bulk actions that don't require a reason
 * (Allocate, Mark Returned). For actions that need notes + reasonCode,
 * use <BulkReasonModal /> instead.
 */
export const BulkConfirmModal: React.FC<BulkConfirmModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  isLoading = false,
  count,
  actionLabel,
  title,
  description,
  confirmLabel,
  icon = 'ri-question-line',
  iconTone = 'primary',
  variant = 'primary',
}) => {
  return (
    <FormModal
      open={isOpen}
      onClose={onClose}
      icon={<i className={icon} />}
      iconTone={iconTone}
      title={title}
      subtitle={`${count} ${count === 1 ? 'delivery' : 'deliveries'} selected`}
      size="sm"
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button
            tone="neutral"
            style="stroke"
            size="sm"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            tone={variant === 'danger' ? 'destructive' : 'primary'}
            size="sm"
            onClick={() => onConfirm()}
            disabled={isLoading}
            loading={isLoading}
          >
            {isLoading ? 'Processing…' : confirmLabel}
          </Button>
        </div>
      }
    >
      <p className="text-sm text-text-sub-600 leading-relaxed">{description}</p>
      <p className="mt-3 text-[12px] text-text-sub-600">
        This will apply{' '}
        <span className="font-semibold text-text-strong-950">
          {actionLabel}
        </span>{' '}
        to {count} {count === 1 ? 'delivery' : 'deliveries'} at once.
      </p>
    </FormModal>
  );
};

export default BulkConfirmModal;
